import React, { useEffect, useState } from "react";
import { Bell, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { enablePush, getPushStatus, isPushSupported, pushAvailability } from "@/lib/push";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";

const LOGIN_COUNT_KEY = "gv_login_count";
const AUTO_PROMPT_KEY = "gv_push_autoprompt_shown";
const DISMISSED_KEY = "gv_push_autoprompt_dismissed";

/**
 * Auto-prompt modal shown on the 2nd login only (less intrusive).
 * Counter `gv_login_count` increments on every authed mount; the modal
 * appears ONCE when the counter reaches 2 — unless the user is already
 * subscribed, has denied permission, or has closed the modal before.
 */
export default function PushAutoPrompt() {
  const { user } = useAuth();
  const { t } = useT();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !isPushSupported()) return;
    const raw = parseInt(localStorage.getItem(LOGIN_COUNT_KEY) || "0", 10) || 0;
    const next = raw + 1;
    localStorage.setItem(LOGIN_COUNT_KEY, String(next));

    if (
      next !== 2 ||
      localStorage.getItem(AUTO_PROMPT_KEY) === "1" ||
      localStorage.getItem(DISMISSED_KEY) === "1"
    ) return;

    (async () => {
      try {
        // Do not surface the modal if push is not configured server-side (no VAPID key).
        const avail = await pushAvailability();
        if (!avail.configured) return;
        const s = await getPushStatus();
        if (s.subscribed || s.permission === "denied") return;
        setVisible(true);
        localStorage.setItem(AUTO_PROMPT_KEY, "1");
      } catch { /* ignore */ }
    })();
  }, [user]);

  if (!visible) return null;

  const close = (persistDismiss = false) => {
    if (persistDismiss) localStorage.setItem(DISMISSED_KEY, "1");
    setVisible(false);
  };

  const activate = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success(t("push.toastEnabled"));
      close(false);
    } catch (e) {
      // Silently dismiss when the server has no VAPID key configured.
      if (e?.code === "PUSH_NOT_CONFIGURED") {
        close(true);
      } else {
        toast.error(e?.message || t("push.toastError"));
      }
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="push-autoprompt"
      className="fixed inset-0 z-[100] flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm p-4 overlay-safe pb-[calc(1rem_+_var(--safe-bottom))]"
    >
      <div className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden">
        <button
          onClick={() => close(true)}
          aria-label={t("common.cancel")}
          data-testid="push-autoprompt-close"
          className="absolute top-3 right-3 p-1.5 rounded-full text-[#8B95A1] hover:bg-[#FBF7F3]"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="p-6 md:p-8 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-[#E5276A] to-[#C01661] flex items-center justify-center">
            <Bell className="w-6 h-6 text-white" />
          </div>
          <h2 className="font-display text-2xl md:text-3xl text-[#1F3A5F] mt-4 leading-tight">
            {t("pushPrompt.title")}
          </h2>
          <p className="text-sm text-[#4A5568] mt-3 leading-relaxed">
            {t("pushPrompt.body")}
          </p>
          <div className="mt-6 flex flex-col gap-2">
            <button
              onClick={activate}
              disabled={busy}
              data-testid="push-autoprompt-activate"
              className="inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661] disabled:opacity-60"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("pushPrompt.activate")}
            </button>
            <button
              onClick={() => close(true)}
              className="inline-flex items-center justify-center px-5 py-2 text-xs font-bold text-[#8B95A1] hover:text-[#E5276A]"
            >
              {t("pushPrompt.later")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
